import dayjs, { type Dayjs } from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import utc from "dayjs/plugin/utc"

import { DATE_FORMAT } from "../../../utils/date/date.ts"

dayjs.extend(utc)
dayjs.extend(customParseFormat)

/**
 * The one "any two of Start/End/Duration ⇒ the third, last-edit-wins" engine,
 * shared by the Lead Workspace's own milestones, the Proposal/Contract
 * editor's Project Overview section, and each Proposal/Contract milestone
 * row. Pure, framework-free (no React/antd) so it is trivially unit-testable
 * and reusable from any caller — a Form-based one wires it up with an
 * `isUpdatingRef` re-entrancy guard the way `PaymentScheduleBlock.tsx` wires
 * its own two-field pair.
 *
 * Contract, mirrored from `lead-scope-milestones.ts`'s original days-only
 * engine (now itself migrated onto this module):
 * - Last edit wins, and one edit derives AT MOST one other field: a date
 *   fills the duration when the opposite date is present, and a duration
 *   fills whichever date is missing (the end when a start exists, else the
 *   start from the end).
 * - Inclusive day counting: a schedule that starts and ends on the same day
 *   lasts one day (`diff + 1`), never the exclusive convention
 *   `ContractValidation/calculate-contract-validation.ts` still uses today.
 * - An end before its start yields no derived duration rather than a
 *   negative one — the field just typed is kept, the other two are left
 *   untouched.
 * - Clearing a field never re-derives anything else, and an incomplete
 *   schedule (only one of the three ever filled) is a valid, permanent state
 *   — nothing here is ever called to "complete" a schedule on load; it only
 *   runs from an actual edit.
 * - A date-pair-derived duration is always expressed in whole days, never
 *   snapped into weeks/months/years — two dates alone carry no information
 *   about which unit the user intends, and rounding a day count into, say,
 *   weeks would silently drop days (10 days ⇒ "1 week" loses 3). Duration ⇒
 *   date derivation, by contrast, IS unit-aware: "2 weeks" from a Start
 *   fills an End 14 inclusive days later.
 */

type DurationUnit = "days" | "weeks" | "months" | "years"

const DURATION_UNITS: DurationUnit[] = ["days", "weeks", "months", "years"]

/**
 * Same table `ScopeOfWorkBlockFooter.tsx` already assumed inline and
 * `ContractValidation/calculate-contract-validation.ts` deliberately omits
 * "years" from (see that file's own docstring) — this module is the one
 * that adds full four-unit support.
 */
const DAYS_PER_UNIT: Record<DurationUnit, number> = {
  days: 1,
  weeks: 7,
  months: 30,
  years: 365
}

const isDurationUnit = (value: unknown): value is DurationUnit =>
  typeof value === "string" && (DURATION_UNITS as string[]).includes(value)

/** Unrecognized/absent input normalizes to "days" — the table's own default. */
const normalizeDurationUnit = (unit?: string | null): DurationUnit => {
  const normalized = String(unit ?? "days")
    .trim()
    .toLowerCase()
  return isDurationUnit(normalized) ? normalized : "days"
}

/**
 * The wire format is a bare calendar day, so the maths runs in UTC where a
 * day is always 24 hours — a local-time diff across a DST switch is off by
 * one. Tolerant of a raw `Dayjs` too (not just the canonical string) — an
 * antd `DatePicker`'s own Form.Item binding stores whatever its `onChange`
 * hands it, which is a local-time `Dayjs`, not yet normalized, until a
 * caller formats it at submit time. A `Dayjs` input is read by its own
 * local calendar-day components (never its UTC instant), so a selection
 * near a timezone's day boundary can't shift a day.
 */
const parseCalendarDay = (value?: string | Dayjs | null): Dayjs | null => {
  if (!value) return null
  const source = dayjs.isDayjs(value) ? value.format(DATE_FORMAT) : value
  const day = dayjs.utc(source, DATE_FORMAT, true)
  return day.isValid() ? day : null
}

const formatCalendarDay = (day: Dayjs): string => day.format(DATE_FORMAT)

/** Inclusive: a schedule that starts and ends on the same day lasts one day. */
const inclusiveDaysBetween = (start: Dayjs, end: Dayjs): number =>
  end.diff(start, "day") + 1

/**
 * A value+unit duration converted to whole calendar days, or `null` when the
 * value is not usable at all (missing, not finite, or floors to less than a
 * full day) — `null` always means "cannot be normalized", never "zero".
 */
const durationToDays = (
  duration?: number | null,
  unit?: string | null
): number | null => {
  if (duration == null || !Number.isFinite(duration)) return null
  const days = Math.floor(duration) * DAYS_PER_UNIT[normalizeDurationUnit(unit)]
  return days > 0 ? days : null
}

interface ScheduleTriple {
  startDate?: string | null
  endDate?: string | null
  duration?: number | null
  durationUnit?: string | null
}

/**
 * Editing Start: fills Duration (always in days, see module docstring) when
 * an End is already present; otherwise only Start itself changes.
 */
const withStartDate = (
  schedule: ScheduleTriple,
  startDate?: string | null
): ScheduleTriple => {
  const next: ScheduleTriple = { ...schedule, startDate: startDate ?? null }

  const start = parseCalendarDay(startDate)
  const end = parseCalendarDay(schedule.endDate)
  if (!start || !end) return next

  const days = inclusiveDaysBetween(start, end)
  return days > 0 ? { ...next, duration: days, durationUnit: "days" } : next
}

/**
 * Editing End: fills Duration (always in days) when a Start is already
 * present; otherwise only End itself changes.
 */
const withEndDate = (
  schedule: ScheduleTriple,
  endDate?: string | null
): ScheduleTriple => {
  const next: ScheduleTriple = { ...schedule, endDate: endDate ?? null }

  const start = parseCalendarDay(schedule.startDate)
  const end = parseCalendarDay(endDate)
  if (!start || !end) return next

  const days = inclusiveDaysBetween(start, end)
  return days > 0 ? { ...next, duration: days, durationUnit: "days" } : next
}

/**
 * Editing Duration (value and/or unit): fills the End when a Start is
 * already present, else fills the Start from the End. Neither date is
 * touched when the schedule has no anchor date yet, or the duration itself
 * doesn't normalize to a usable day count.
 */
const withDuration = (
  schedule: ScheduleTriple,
  duration?: number | null,
  durationUnit?: string | null
): ScheduleTriple => {
  const unit = normalizeDurationUnit(durationUnit ?? schedule.durationUnit)
  const next: ScheduleTriple = {
    ...schedule,
    duration: duration ?? null,
    durationUnit: duration == null ? (schedule.durationUnit ?? null) : unit
  }

  const days = durationToDays(duration, unit)
  if (days == null) return next

  const start = parseCalendarDay(schedule.startDate)
  if (start) {
    return { ...next, endDate: formatCalendarDay(start.add(days - 1, "day")) }
  }

  const end = parseCalendarDay(schedule.endDate)
  if (end) {
    return {
      ...next,
      startDate: formatCalendarDay(end.subtract(days - 1, "day"))
    }
  }

  return next
}

interface DurationTotalEntry {
  duration?: number | null
  durationUnit?: string | null
}

interface DurationTotalResult {
  /** Sum of every entry whose duration normalizes to a usable day count. */
  totalDays: number
  /** How many entries contributed a usable duration. */
  definedCount: number
  /** How many entries have no usable duration at all. */
  missingCount: number
}

/**
 * Sums a list's normalized durations while keeping "has no duration" visible
 * instead of folding it into the total as a silent 0 — the fix
 * `ScopeOfWorkBlockFooter.tsx` needs so "Total: 23 days" never looks complete
 * when 7 of 10 milestones were never given a duration at all.
 */
const totalDurationDays = (
  entries: DurationTotalEntry[]
): DurationTotalResult =>
  entries.reduce<DurationTotalResult>(
    (result, entry) => {
      const days = durationToDays(entry.duration, entry.durationUnit)
      if (days == null) {
        return { ...result, missingCount: result.missingCount + 1 }
      }
      return {
        totalDays: result.totalDays + days,
        definedCount: result.definedCount + 1,
        missingCount: result.missingCount
      }
    },
    { totalDays: 0, definedCount: 0, missingCount: 0 }
  )

interface DurationParts {
  years: number
  months: number
  days: number
}

/** Breaks a whole day count into a years/months/days display triple. */
const splitDaysIntoParts = (totalDays: number): DurationParts => {
  const years = Math.floor(totalDays / DAYS_PER_UNIT.years)
  const remainingAfterYears = totalDays % DAYS_PER_UNIT.years
  const months = Math.floor(remainingAfterYears / DAYS_PER_UNIT.months)
  const days = remainingAfterYears % DAYS_PER_UNIT.months
  return { years, months, days }
}

export {
  DAYS_PER_UNIT,
  DURATION_UNITS,
  type DurationUnit,
  type ScheduleTriple,
  type DurationTotalEntry,
  type DurationTotalResult,
  type DurationParts,
  isDurationUnit,
  normalizeDurationUnit,
  parseCalendarDay,
  formatCalendarDay,
  inclusiveDaysBetween,
  durationToDays,
  withStartDate,
  withEndDate,
  withDuration,
  totalDurationDays,
  splitDaysIntoParts
}
